import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { FeedbackModal } from '@bingads-webui-campaign-react/recommendation-feedback-modal';
import {
  CHANNEL_TYPES,
  APPLY_ACTION_TYPES,
  withTrack,
  isCompetition,
} from '@bingads-webui-campaign/recommendation-core';
import {
  ToolbarButton,
  DownloadToolbarButton,
} from '@bingads-webui-campaign-react/recommendation-visuals';

class detailsToolbar extends React.PureComponent {
  static propTypes = {
    title: PropTypes.string,
    i18n: PropTypes.PropTypes.shape({
      getString: PropTypes.func.isRequired,
    }).isRequired,
    viewSummary: PropTypes.func.isRequired,
    getUserInputs: PropTypes.func.isRequired,
    type: PropTypes.string.isRequired,
    aggregateLevel: PropTypes.string,
    campaignAdGroups: PropTypes.arrayOf(PropTypes.object).isRequired,
    track: PropTypes.func.isRequired,
    callback: PropTypes.func.isRequired,
    children: PropTypes.element,
    total: PropTypes.number.isRequired,
    readOnly: PropTypes.bool.isRequired,
    context: PropTypes.shape({
      Level: PropTypes.string.isRequired,
    }).isRequired,
    guid: PropTypes.string.isRequired,
    logExpInfo: PropTypes.func.isRequired,
    updateToolbar: PropTypes.func,
    initialToolbarConfig: PropTypes.objectOf(PropTypes.any),
    scope: PropTypes.string,
  };

  static defaultProps = {
    title: '',
    children: null,
    aggregateLevel: null,
    updateToolbar: _.noop,
    initialToolbarConfig: null,
    scope: null,
  };

  state = {
    modal: {
      isOpen: false,
    },
  };

  componentDidMount() {
    const {
      i18n,
      viewSummary,
      total,
      readOnly,
      updateToolbar,
      initialToolbarConfig,
      scope,
    } = this.props;
    const shouldDisable = total === 0;
    const channel = isCompetition(this.props.type) ?
      CHANNEL_TYPES.COMPETITION : CHANNEL_TYPES.RECOMMENDATION;
    const enableToolbarVNext = initialToolbarConfig;
    if (enableToolbarVNext) {
      let toolbarItems = [{
        id: 'back-to-summary',
        content: this.getBackToSummaryButton(viewSummary, i18n),
      }];
      if (scope === 'AdGroup') {
        toolbarItems.push({
          id: 'expander',
          content: <div className="expander" />,
        });
      }
      toolbarItems = toolbarItems.concat([
        {
          id: 'apply-all',
          content: this.getApplyAllButton(shouldDisable, readOnly, i18n),
        },
        {
          id: 'dismiss-all',
          content: this.getDismissAllButton(shouldDisable, readOnly, i18n),
        },
        {
          id: 'button-download',
          content: this.getDownloadButton(shouldDisable, channel),
        },
        {
          id: 'reject-all',
          content: this.getRejectAllButton(shouldDisable, i18n),
        },
      ]);
      updateToolbar(toolbarItems, true);
    }
  }

  getBackToSummaryButton = (viewSummary, i18n) => (
    <ToolbarButton className="btn btn-navigate-back btn-default" onClick={viewSummary}>
      {<span className="iconba iconba-Back" />}
      {i18n.getString('Details_View_Back_To_Summary')}
    </ToolbarButton>
  )

  getApplyAllButton = (shouldDisable, readOnly, i18n) => (
    <ToolbarButton className="btn btn-primary" onClick={this.applyAll} disabled={shouldDisable || readOnly}>
      {<span className="iconba iconba-CheckMark" /> }
      {i18n.getString('Details_View_Apply_All')}
    </ToolbarButton>
  )

  getDismissAllButton = (shouldDisable, readOnly, i18n) => (
    <ToolbarButton className="btn btn-secondary" onClick={this.dismissAll} disabled={shouldDisable || readOnly}>
      {<span className="iconba iconba-Blocked" />}
      {i18n.getString('Details_View_Dismiss_All')}
    </ToolbarButton>
  )

  getDownloadButton = (shouldDisable, channel) => (
    <DownloadToolbarButton
      disabled={shouldDisable}
      channel={channel}
      {..._.pick(this.props, 'context', 'i18n', 'downloadService', 'filterService', 'type')}
    />
  )

  getRejectAllButton = (shouldDisable, i18n) => (
    <ToolbarButton className="btn btn-default" onClick={this.handleReject} disabled={shouldDisable}>
      { <span className="iconba iconba-Blocked" />}
      {i18n.getString('Details_View_Reject_All')}
    </ToolbarButton>
  )

  buildDefaultRequestOptions = () => (
    {
      type: this.props.type,
      campaignAdGroups: this.props.campaignAdGroups,
      channel: isCompetition(this.props.type) ?
        CHANNEL_TYPES.COMPETITION : CHANNEL_TYPES.RECOMMENDATION,
      applyOpportunitiesLevel: this.props.aggregateLevel,
      context: this.props.context,
      guid: this.props.guid,
    }
  )

  applyAll = () => {
    const defaultOptions = this.buildDefaultRequestOptions();
    const userInputs = this.props.getUserInputs();
    const ops = _.extend(defaultOptions, {
      userAction: APPLY_ACTION_TYPES.ACCEPT,
    }, userInputs && { userInputs });

    // eslint-disable-next-line no-alert, no-restricted-globals
    if (!confirm(this.props.i18n.getString('Recommendation_Apply_All_Confirm', {
      count: this.props.total,
    }))) {
      return;
    }

    const action = `${this.props.total} recommendations applied`;
    this.props.logExpInfo(action);

    this.props.track(ops);
  }

  dismissAll = () => {
    const defaultOptions = this.buildDefaultRequestOptions();
    const ops = _.extend(defaultOptions, {
      userAction: APPLY_ACTION_TYPES.DISMISS,
    });

    // eslint-disable-next-line no-alert, no-restricted-globals
    if (!confirm(this.props.i18n.getString('Recommendation_Dismiss_All_Confirm', {
      count: this.props.total,
    }))) {
      return;
    }

    this.props.track(ops);
  }

  handleReject = () => {
    const defaultOptions = this.buildDefaultRequestOptions();

    this.setState({
      modal: {
        isOpen: true,
        recommendationDescription: this.props.title,
        isProvideFeedbackOnly: false,
        data: defaultOptions,
      },
    });
  }

  handleFeedbackSubmit = () => {
    this.setState({ modal: { isOpen: false } }, () => {
      this.props.callback();
    });
  };

  handleFeedbackCancel = () => {
    this.setState({ modal: { isOpen: false } });
  };

  render() {
    const {
      i18n,
      viewSummary,
      children,
      total,
      readOnly,
      initialToolbarConfig,
    } = this.props;
    const shouldDisable = total === 0;
    const channel = isCompetition(this.props.type) ?
      CHANNEL_TYPES.COMPETITION : CHANNEL_TYPES.RECOMMENDATION;
    const enableToolbarVNext = initialToolbarConfig;
    const toolbar = enableToolbarVNext ? (
      <div role="toolbar">
        {children}
      </div>
    ) : (
      <div role="toolbar" className="toolbar">
        {this.getBackToSummaryButton(viewSummary, i18n)}
        {this.getApplyAllButton(shouldDisable, readOnly, i18n)}
        {this.getDismissAllButton(shouldDisable, readOnly, i18n)}
        {this.getDownloadButton(shouldDisable, channel)}
        { /* reject is not disabled for readOnly but the modal submit button is */ }
        {this.getRejectAllButton(shouldDisable, i18n)}
        {children}
      </div>
    );
    return (
      <div className={`recommendation-details-toolbar ${shouldDisable ? 'no-data' : ''}`}>
        {toolbar}
        <FeedbackModal
          {...this.state.modal}
          i18n={i18n}
          onCancel={this.handleFeedbackCancel}
          callback={this.handleFeedbackSubmit}
          {..._.pick(this.props, 'dataService', 'el', 'readOnly')}
        />
      </div>
    );
  }
}

export const DetailsToolbar = withTrack(/* withAll */ true)(detailsToolbar);
